/**
 * @fileOverView
 * @description Utilities, basic functions attached to app<br>
 * @copyright 2015-present Flatirontek LLC
 **/

'use strict';
const moment = require('moment-timezone');
const Clipboard = require('clipboard');
const Rating = require('./rating_manager');
const app = require('ampersand-app');
//shared seat helpers; the ladder itself lives in the tier table server-side,
//and this account's cap arrives as `seats` on the profile and meter responses
const orghelpers = require('../../../lib/orghelpers');
const logText = require('../../../lib/logtext');
const limits = require('../../../lib/notificationlimits');


module.exports = {

    //active-context token: the org token while managing an org, else personal.
    //Management surfaces (vybits, logs, sounds, meter) use this.
    setAuthTokenHeader: function(){
        return {
            headers: {
                'Authorization': app.auth.orgToken || app.auth.jwtToken
            }
            /* , xhrFields: {
                'withCredentials': true
            } */
        };
    },

    //continue URL for Firebase email actions; carries an in-progress third-party authorization so the user lands back on the consent page
    signInContinueUrl: function(){
        let url = app.platform === "browser" ? window.location.origin : "https://" + app.config.dynamicLinkDomain;
        if(app.serviceAuth && window.localStorage.getItem('serviceAuth') && window.localStorage.getItem('state')){
            url += '?client_id=' + window.localStorage.getItem('serviceAuth') + '&state=' + window.localStorage.getItem('state');
            const redirectUri = window.localStorage.getItem('redirect_uri');
            if(redirectUri) url += '&redirect_uri=' + redirectUri;
            ['response_type', 'code_challenge', 'code_challenge_method', 'scope', 'nonce'].forEach(key => {
                const value = window.localStorage.getItem(key);
                if(value) url += '&' + key + '=' + encodeURIComponent(value);
            });
        }
        return url;
    },

    emailActionSettings: function(){
        return {url: this.signInContinueUrl(), handleCodeInApp: true, linkDomain: "signin.vybit.net"};
    },

    //marketing-site URL for the current env (dev.www / staging.www / www)
    wwwUrl: function(path){
        return 'https://' + app.config.wwwHost + (path || '');
    },

    //for text from users or third-party clients that ends up in an innerHTML binding
    escapeHtml: function(value){
        return logText.escapeHtml(String(value == null ? '' : value));
    },

    //every value a third-party authorization keeps in localStorage between page loads
    serviceAuthKeys: ['serviceAuth', 'serviceAuthType', 'state', 'redirect_uri', 'response_type', 'dtSet', 'oauth2ServiceName', 'oauth2SelfRegistered', 'code_challenge', 'code_challenge_method', 'scope', 'nonce'],

    //an app's self-chosen name, cut for headers and titles so a long one can't push
    //the consent buttons down; the unverified note still quotes it whole
    shortAppName: function(name){
        const chars = Array.from(name || '');
        return chars.length > 40 ? chars.slice(0, 40).join('').replace(/\s+$/, '') + '…' : name;
    },

    unverifiedNameNote: function(name){
        return '\u201c' + name + '\u201d is the name this app gave itself. Vybit has not verified it.';
    },

    //anchor that opens outside the SPA; native apps need InAppBrowser
    externalLinkHtml: function(url, text){
        if(app.platform == "app"){
            return '<a href="javascript:void(0)" onclick=\'cordova.InAppBrowser.open("' + url + '", "_system");\'>' + text + '</a>';
        }
        return '<a href="' + url + '" target="_blank">' + text + '</a>';
    },

    orgSeatCap: orghelpers.seatCap,

    //counts the live collection unless a search has filtered it, in which case
    //the last full count stands
    orgSeats: function(){
        var used = (app.orgMembers && !app.orgMembers.searchQuery) ? app.orgMembers.length
            : (app.orgSeatsUsed || 0);
        return orghelpers.seatSummary(app.profile ? app.profile.seats : undefined, used);
    },

    showMemberSearch: orghelpers.showMemberSearch,

    //personal token ALWAYS: identity and receiver surfaces (profile, own
    //subscriptions, device, billing, org admin, push/sound playback)
    setPersonalAuthTokenHeader: function(){
        return {
            headers: {
                'Authorization': app.auth.jwtToken
            }
        };
    },

    playSound : function(sk,isVyb=false,logParams=null){
        if(sk==null || sk=='none'){
            app.eventLog.add('playing sound of none');
            app.utils.stopShakes();
            return;
        }

        let existingSound = (app.usedSounds && app.usedSounds.get(sk,'key')) || (app.sounds && app.sounds.get(sk,'key'))

        if(existingSound) {
            existingSound.playSound(isVyb,logParams);
        } else {

            try {
                var sound = new app.Sound({key: sk, type: 'mp3'});
                sound.fetch({
                    success: function(model,response){
                        app.sounds.add(model);
                        if(app.vybs.get(model.key,'soundKey')!=undefined){
                            app.vybs.get(model.key,'soundKey').set('sound',model);
                            app.vybs.get(model.key,'soundKey').set('soundName',model.name);
                        }
                        model.playSound(isVyb,logParams);
                    },
                    error: function(model, error){
                        app.eventLog.add('cannot retrieve sound ' + sk);
                    }
                });
            } catch(err) {
                app.eventLog.add('error playing sound ' + sk + ': ' + err.message);
            }
        }
    },

    retrieveSound : function(sk){

        if(sk==null || sk=='none'){
            app.eventLog.add('retrieving sound of none.')
        }

        if(app.sounds.get(sk,'key')){
            if(app.vybs.get(sk,'soundKey')!=undefined){
                app.vybs.get(sk,'soundKey').set('soundName',app.sounds.get(sk,'key').name);
            }
        }else{
            var sound = new app.Sound({key:sk});
            sound.fetch({
                success: function(model,response){
                    app.sounds.add(model);
                    if(app.vybs.get(model.key,'soundKey')!=undefined){
                        app.vybs.get(model.key,'soundKey').set({'soundName':model.name});
                    }
                },
                error: function(){
                    app.eventLog.add('cannot retrieve sound ' + sk);
                }
            });
        };
        return 1;
    },

    loadVybWithSound: function(sound){

        //save chosen sound from demo sound page
        if(!app.auth.authenticated && app.vybs==undefined){
            this.cacheChosenDemoSound(sound);
            return;
        }

        if(sound.mode=="sounds"){
            var vyb = app.vybs.get(sound.vybKey);
        }else{
            if(sound.vybKey!=null) return;
            var vyb = app.vyb;
            var oldSoundKey = vyb.soundKey;
            if(app.sounds.get(oldSoundKey)){
                var oldSound = app.sounds.get(oldSoundKey);
                console.log('changing old sound ' + oldSound.name);
                app.sounds.get(oldSoundKey).set('mode','sounds');
                app.sounds.get(oldSoundKey).set('toggle',oldSound.toggle ? false : true);
            }
            app.usedSounds.remove(oldSoundKey,'key');
            vyb.patch({soundKey: sound.key},null);
            app.usedSounds.add(sound);
        }

        var options = {model:vyb};
        if(sound.vybKey==null){
            options.soundKey = sound.key;
            options.soundName = sound.soundName;
            options.action = "showSound";
        }

        console.log('loading vyb page');

        var vybpageview =   new app.VybPageView(options);
        app.pageSwitcher.set(vybpageview);
    },

    loadVybFollowWithSound: function(sound){
        if(sound.mode=="sounds"){
            var vybfollow = app.vybsfollowing.get(sound.vybKey);
        }else{
            if(sound.vybKey!=null) return;
            var vybfollowing = app.vybfollowing;
            var oldSoundKey = vybfollowing.soundKey;
            app.usedSounds.remove(oldSoundKey,'key');
            vybfollowing.patch({soundKey:sound.key});
            app.usedSounds.add(sound);
        }

        var options = {model:vybfollowing};
        if(sound.vybKey==null){
            options.soundKey = sound.key;
            options.action = "showSound";
        }

        var vybfollowingpageview =   new app.VybFollowingPageView(options);
        app.pageSwitcher.set(vybfollowingpageview);
    },

    cacheChosenDemoSound: function(sound){
        console.log('caching chosen demo sound ' + sound.name + ', ' + sound.key);
        window.localStorage.setItem('demoSoundKey', sound.key);
        window.localStorage.setItem('demoSoundName', sound.name);
        window.location.reload();
        return;
    },

    captureQueryParams: function(){
        //parse out parameters
        var params = {}, queries, temp, i, l;
        queries = app.queryString.split("&");
        for ( i = 0, l = queries.length; i < l; i++ ) {
            temp = queries[i].split('=');
            params[temp[0]] = temp[1];
        }
        app.queryParams = params;

        //remove query string from location, except for dev env
        app.incomingUrl = window.location.href;
        if (app.incomingUrl.indexOf("?") > 0 && app.config.env!="developmentOFF") {
            var updatedUri = app.incomingUrl.substring(0, app.incomingUrl.indexOf("?"));
            window.history.replaceState({}, document.title, updatedUri);
            app.queryString = "";
        }

        //store subscription key
        if(app.queryParams.sk!=undefined){
            window.localStorage.setItem('subscriptionKey',app.queryParams.sk);
        }
    },

    captureSubscriptionKeyFromDeepLink: function(link){
        console.log('capturing sk from deep link ' + link);
        var parts = link.split("sk=");
        var sk = parts.pop();
        console.log('sk is ' + sk);
        window.localStorage.setItem('subscriptionKey',sk);
        if(app.auth.authenticated) this.processSubscription();
    },

    processSubscription: function(){
        var sk = window.localStorage.getItem('subscriptionKey');
        if(sk==null){
            console.log('no subscriptionKey');
            return;
        }
        app.eventLog.add('subscribing to ' + sk);
        var subscription = new app.VybFollowing();
        subscription.set('subscriptionKey',sk);
        window.localStorage.removeItem('subscriptionKey');
        subscription.save({},{
            success: (model,response) => {
                console.log("successfully subscribed to " + sk);
                app.renderPage();
            },
            error: (err) => {
                console.log("error subscribing to " + sk + ": ");
                console.dir(err);
            }
        });
    },
    
    checkForUnplayedVybs: function(){
        app.eventLog.add('begin check for unplayed vybs');
        app.logs.getLogsWithUnplayedSounds();
    },

    stopShakes: function(){
        app.eventLog.add('trying to stop shakes');
        var elements = document.getElementsByClassName('vybshake');
        while(elements.length > 0){
            elements[0].classList.remove('vybshake');
        }
    },

    startSpinner: function(){
        var spinnerDiv = document.querySelector("div.vspinner");
        if(spinnerDiv==null) return;
        spinnerDiv.classList.add("ispinner");
        spinnerDiv.classList.add("gray");
        spinnerDiv.classList.add("animating");

        var cover = document.querySelector('div.vcover');
        if(cover!=null) cover.classList.add("voverlay");

    },

    stopSpinner: function(){
        var spinnerDiv = document.querySelector("div.vspinner");
        if(spinnerDiv!=null){
            spinnerDiv.classList.remove("ispinner");
            spinnerDiv.classList.remove("gray");
            spinnerDiv.classList.remove("animating");
        }

        var cover = document.querySelector('div.vcover');
        if(cover!=null) cover.classList.remove("voverlay");

        app.utils.setDisplayMode();
    },

    cancelBubble: function(e) {
        if(e!=undefined && e.preventDefault) e.preventDefault();
        var evt = e ? e:window.event;
        if (evt.stopPropagation)    evt.stopPropagation();
        if (evt.cancelBubble!=null) evt.cancelBubble = true;
        return false;
    },

    openInSystemBrowser: function(url, event) {
        if (typeof cordova !== "undefined" && cordova.InAppBrowser) {
            this.cancelBubble(event);
            cordova.InAppBrowser.open(url, "_system");
            return false;
        }
        return true; // Fallback to normal link behavior if Cordova not available
    },

    infiniteScroll : function(view){

        var wait = 1000;
        var now = Date.now();

        if (view.el.scrollHeight - view.el.scrollTop - 20 <= view.el.clientHeight) {

            //throttle
            var diff = now - view.lastLoad;
            if(diff < wait){
                return;
            }else{
                view.lastLoad = now;
            }

            var data = {offset:view.collection.models.length};
            if(app[view.entity].searchQuery!=undefined && app[view.entity].searchQuery.length>0) data.query = app[view.entity].searchQuery;
            app.utils.startSpinner();
            view.collection.fetch({
                remove:false,
                reset:false,
                add:true,
                data:data,
                success: function(){
                    app.utils.stopSpinner();
                    console.log('loaded more ' + view.entity);
                    if(view.entity=="logs") app.meter.fetch();
                },
                error: function(){
                    app.utils.stopSpinner();
                    console.log('error loading new ' + view.entity);
                },
            });
        }

        if(view.el.scrollTop==0){

            //throttle
            var diff = now - view.lastLoad;
            if(diff < wait){
                return;
            }else{
                view.lastLoad = now;
            }

            app.utils.startSpinner();
            data = {};
            if(app[view.entity].searchQuery!=undefined && app[view.entity].searchQuery.length>0) data.query = app[view.entity].searchQuery;
            view.collection.fetch({
                data: data,
                success: function(){
                    app.utils.stopSpinner();
                    if(view.entity=="logs"){
                        app.vybsfollowing.fetch();
                        app.meter.fetch();
                    }
                },
                error: function(){
                    app.utils.stopSpinner();
                }
            });
        }
    },

    switchToVybPage: function(entity){

        // Determine the key to use
        var vybKey = entity.vybKey || entity.key;

        // Check if this is the user's own vyb first (owner view)
        if(vybKey!=null && app.vybs.get(vybKey)!=undefined){
            var pageview = new app.VybPageView({model:app.vybs.get(vybKey)});
            app.pageSwitcher.set(pageview);
            return;
        }

        //vybfollow (subscriber view)
        if(entity.followKey!=null){

            //already loaded
            if(app.vybsfollowing.get(entity.followKey)!=undefined){
                var pageview =   new app.VybFollowingPageView({model:app.vybsfollowing.get(entity.followKey)});
                app.pageSwitcher.set(pageview);

            //fetch it
            }else{
                var vf = new app.VybFollowing({key:entity.followKey});
                vf.fetch({
                    success: function(model,response){
                        app.vybsfollowing.add(model);
                        var pageview =   new app.VybFollowingPageView({model:model});
                        app.pageSwitcher.set(pageview);
                    },
                    error: function(model,response,error){
                        console.log('error fetch vybfollowing ' + entity.followKey);
                    }
                });
            }

        //vyb (fetch if not loaded)
        }else if(entity.key!=null || entity.vybKey!=null){

            if(entity.vybKey!=null) entity.key = entity.vybKey;

            //already loaded
            if(app.vybs.get(entity.key)!=undefined){
                var pageview =   new app.VybPageView({model:app.vybs.get(entity.key)});
                app.pageSwitcher.set(pageview);

            //fetch it
            }else{
                var vyb = new app.Vyb({key:entity.key});
                vyb.fetch({
                    success: function(model,response){
                        app.vybs.add(model);
                        var pageview =   new app.VybPageView({model:model});
                        app.pageSwitcher.set(pageview);
                    },
                    error: function(model,response,error){
                        console.log('error fetch vyb ' + entity.key);
                    }
                });
            }
        }
    },

    switchToSubscriptionPage: function(subscription){
        // The public browse page (/public) loads the marketing www.css chrome
        // into the SPA shell. An in-SPA pageSwitcher swap would keep that fixed
        // header in the document and hide the top of the subscription card, so
        // from /public navigate to the canonical /subscription/<key> URL with a
        // full load into the clean subscription shell instead. Every other
        // caller (e.g. the in-app vyb list) keeps the smooth pageSwitcher swap.
        if(window.location.pathname=="/public"){
            window.location.href = "/subscription/" + subscription.key;
            return;
        }
        var pageview =   new app.SubscriptionCardPageView({model:subscription});
        app.pageSwitcher.set(pageview);
    },

    reviewVyb: function(vybKey){
      this.cancelBubble();
      this.switchToVybPage({vybKey:vybKey});
    },

    //assumes mp3
    playSoundFileFromKey: function(soundKey,soundType="mp3"){

        if(soundKey==null){
            this.stopShakes();
            return;
        }

        var soundFile = soundKey + "." + soundType;
        this.playSoundFile(soundFile,soundKey);
    },

    playSoundFile: function(soundFile,soundKey=null){

        var pel = null;
        var el = null;
        if(soundKey!=null){
            if(document.querySelector("[data-sk='"+soundKey+"'], [data-soundkey='"+soundKey+"']")!=null) pel = document.querySelector("[data-sk='"+soundKey+"'], [data-soundkey='"+soundKey+"']");
            if(pel!=null && pel.querySelector("img.vdeltaimg, img.vsubdeltaimg")!=null){
                el = pel.querySelector("img.vdeltaimg, img.vsubdeltaimg");
                el.classList.add("vybshake");
            }
        }

        var file = app.config.soundLib + '/' + soundFile;
        var sobject = new Audio(file);
        sobject.volume = 1.0;
        sobject.onended = function(){
            app.eventLog.add('play sound file ' + soundFile + ' completed');
            if(pel!=null) pel.blur();
            if(el!=null) el.classList.remove("vybshake");
        };
        sobject.play();
    },

    runTicker: function(view){
        var self = this;
        var message = ' search thousands of sounds. for your vybits, ' +
            'for example:. dog bark. chimes. thunder. try it! :). search sounds|';
        var ticker = view.query("input.vsdemo");
        var queue = message.split("");
        var cur = "";

        this.advanceTicker(self,ticker,queue,cur);
    },

    advanceTicker: function(self,ticker,queue,cur){
        var interval = queue[0]=="." ? 1600 : 70;
        (function(self,ticker,queue,cur){
            setTimeout(
                function(){
                    if(ticker==undefined) return;
                    var char = queue.shift();
                    cur = char=="." ? "" : cur + char;
                    if(char=="|") return;
                    ticker.setAttribute("placeholder",cur);
                    self.advanceTicker(self,ticker,queue,cur);
                },
                interval
            );
        }(self,ticker,queue,cur));
    },

    demoSoundSearch: function(){
        var searchQuery = document.querySelector("input.vsdemo").value;
        console.log('squery is ' + searchQuery);
        clearInterval(app.tickerInterval);
        var url = app.config.baseApiUrl + "?p=sounds&squery=" + encodeURIComponent(searchQuery);
        console.log('url is ' + url);
        window.location.href = url;
    },

    dssOnEnter: function(event){
        if(event.keyCode==13){
            this.demoSoundSearch();
        }
    },

    docSectionTitleMap: (section)=>{
        var map = {
            "what_is_vybit" : 'What is Vybit?',
            "vybit_awesome" : 'Why is Vybit AWESOME?',
            "getting_started" : 'Getting Started',
            "create" : 'Vybit Creation and Management',
            "trigger" : 'Triggering Vybits',
            "subscribe" : "Subscribing to Vybits",
            "sharing" : 'Sharing Vybits',
            "connecting_ai": "Connecting Vybit to Your Favorite AI Agent",
            "faqs" : 'FAQs and Troubleshooting',
            "organization" : 'Can an organization share a Vybit account?',
            "plans" : 'Is Vybit is Free?',
            "support" : 'How Do I Contact Vybit Support?',
            "platforms" : 'Services and Platforms to use with Vybit',
            "white_label" : 'White Label Vybit for you App, Service, or Platform',
            "marketing" : 'Market to Your Customers with Vybit',
        };
        return map[section];
    },

    openDocCardfromFrag: function(){
        var map = {
            "what_is_vybit" : 'whatIsVybit',
            "vybit_awesome" : 'vybitAwesome',
            "getting_started" : 'gettingStarted',
            "create" : 'createVyb',
            "trigger" : 'triggerVyb',
            "subscribe" : "subscribeVyb",
            "sharing" : 'sharingVybs',
            "connecting_ai" : 'aiagent',
            "organization" : 'org',
            "plans" : "plans",
            "faqs" : 'faqs',
            "platforms" : 'platforms',
            "support" : 'support',
            "integrations" : 'integrations',
            "marketing" : "market",
            "geofence" : "createVyb"
        };
        var frag = window.location.hash.substring(1);
        var elId = map[frag];

        if(elId!=undefined){

            var a = document.getElementById('S' + elId);
            if(a!=null) a.classList.add('show');

            var q = document.getElementById('H' + elId);
            if(q!=null){
                var container  = document.getElementById('docs');
                var containerTop = container.offsetTop;
                var topPos = q.parentElement.offsetTop;
                var childPos = 0;
                if(document.getElementById(frag)!=null){
                    childPos = document.getElementById(frag).offsetTop - topPos - containerTop + 160;
                }
                console.log('c top is ' + containerTop);
                console.log('topPos is ' + topPos);
                container.scroll({top: topPos - containerTop + childPos});
            }
        }
    },

    retrieveSubscriptionAndDisplay: function(loginModel){
        var subscription = new app.Subscription({key: window.localStorage.getItem('subscriptionKey')});
        subscription.fetch({
           success: function(model,response){
               loginModel.set('loginMessage','Sign In to subscribe to vybit <b>' + model.name + '</b> from <b>'+ model.ownerName +'</b>.');
           }
        });
    },

    removeParentEl: function(event,el){
        this.cancelBubble(event);
        el.parentElement.remove();
    },

    subscribeToVybFromKey: function(subKey=null, el=null){

        if(subKey==null){
            console.log('no subscription key found');
            return;
        }

        console.log('subKey is ' + subKey);

        if(window.location.host==app.config.wwwHost) window.open(app.config.baseApiUrl + "/subscribe/" + subKey);
        else{
            window.localStorage.setItem("subscriptionKey",subKey);
            this.processSubscription();
        }

        if(el!=null) el.blur();
    },

    validServiceClient: function(client_id=app.queryParams.client_id){
        return Object.keys(app.config.serviceAuth).includes(client_id);
    },

    firebaseAuthPrep: function(){
        /* Browsertab */
        window.cordova.plugins.browsertab = {};

        /* Browsertab - Is Available */
        window.cordova.plugins.browsertab.isAvailable = function( success ) {
            window.SafariViewController.isAvailable( success );
        }

        /* Browsertab - Open Url */
        window.cordova.plugins.browsertab.openUrl = function( url ) {
            url += "&nocache=1&buster=" + Math.floor(Math.random() * 1000000000 );
            window.SafariViewController.show( { url: url } , function() { console.log('opening url ' + url); } , function() {} );
        }

        /* Browsertab - Close */
        window.cordova.plugins.browsertab.close = function() {
            window.SafariViewController.hide();
        }
    },

    setDisplayMode: function(){
        if(app.header==undefined) return;
        if(app.header.get('mode')=="day"){
            document.querySelector("body").classList.remove("vnight");
            if(document.querySelector("img.vdeltaimg, img.vbell")!=null) document.querySelectorAll("img.vdeltaimg, img.vbell").forEach(function(el){ el.src = "images/bell1.png";});
        }else{
            document.querySelector("body").classList.add("vnight");
            if(document.querySelector("img.vdeltaimg, img.vbell")!=null) document.querySelectorAll("img.vdeltaimg, img.vbell").forEach(function(el){ el.src = "images/bell1_night.png";});
        }
    },

    isAWebView: function(){

        if(app.platformId!="android"){
            var is_uiwebview = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent);
            return is_uiwebview;

        }else{
            if( navigator.userAgent.includes ('wv')){
                return true;
            }else{
                return false;
            }
        }

        return false;
    },

    createDomElementFromString: function(html){
        var div = document.createElement('DIV');
        div.innerHTML = html;
        return div.firstChild;
    },

    startBackgroundRefresh: async function(){

        if(window.BackgroundFetch==undefined) return;

        var BackgroundFetch = window.BackgroundFetch;

        var onEvent = async function(taskId) {
            app.eventLog.add('BackgroundFetch.onEvent(' +taskId + ') background refresh started');
            app.loadData();
            setTimeout(()=>{
                app.eventLog.add('starting BackgroundFetch.finish(' + taskId + ')');
                BackgroundFetch.finish(taskId);
                },20000);
        };

        var onTimeout = async function(taskId) {
            app.eventLog.add('[BackgroundFetch] TIMEOUT: ', taskId);
            BackgroundFetch.finish(taskId);
        };

        var status = await BackgroundFetch.configure(
            {
                minimumFetchInterval: 60,
                requiredNetworkType: BackgroundFetch.NETWORK_TYPE_ANY
            }, onEvent, onTimeout);
        app.eventLog.add('[BackgroundFetch] configure status: ');
        app.eventLog.add(status);
    },

    // ---- Schedule UI helpers ----

    _readActiveToggles: function(card, sectionClass) {
        var allBtn = card.querySelector(sectionClass + ' .vts-toggle-all');
        if (allBtn && allBtn.classList.contains('active')) return [];
        var values = [];
        card.querySelectorAll(sectionClass + ' .vts-toggle.active:not(.vts-toggle-all)').forEach(function(t) {
            values.push(parseInt(t.dataset.value));
        });
        return values;
    },

    _isContiguousArray: function(arr) {
        if (!arr || arr.length <= 1) return true;
        var sorted = arr.slice().sort(function(a,b){return a-b;});
        for (var i = 1; i < sorted.length; i++) {
            if (sorted[i] - sorted[i-1] !== 1) return false;
        }
        return true;
    },

    _isWeekdays: function(days) {
        return days && days.length === 5 && [1,2,3,4,5].every(function(d){ return days.indexOf(d) >= 0; });
    },

    _createToggleSection: function(sectionClass, labelText, togglesClass, items, selectedValues, extraToggleClass) {
        var section = document.createElement('div');
        section.className = sectionClass;
        var label = document.createElement('div');
        label.className = 'vts-card-row';
        label.innerHTML = '<span class="vts-card-label">' + labelText + '</span>';
        var toggles = document.createElement('div');
        toggles.className = togglesClass;
        for (var i = 0; i < items.length; i++) {
            var btn = document.createElement('span');
            btn.className = 'vts-toggle' + (extraToggleClass ? ' ' + extraToggleClass : '');
            btn.dataset.value = items[i].value;
            btn.textContent = items[i].label;
            if (selectedValues && selectedValues.indexOf(items[i].value) >= 0) btn.classList.add('active');
            toggles.appendChild(btn);
        }
        var allBtn = document.createElement('span');
        allBtn.className = 'vts-toggle vts-toggle-all';
        allBtn.textContent = 'All';
        if (!selectedValues || selectedValues.length === 0 || selectedValues.length === items.length) allBtn.classList.add('active');
        toggles.appendChild(allBtn);
        label.appendChild(toggles);
        section.appendChild(label);
        return section;
    },

    ordinal: function(n) {
        var s = ['th','st','nd','rd'];
        var v = n % 100;
        return n + (s[(v-20)%10] || s[v] || s[0]);
    },

    cronToScheduleConfig: function(cronString) {
        var parts = cronString.trim().split(/\s+/);
        var minField = parts[0], hourField = parts[1], domField = parts[2], monthField = parts[3], dowField = parts[4];

        //the schedule editor writes plain lists; crons from the API and MCP also use ranges (1-5) and steps (*/15)
        var parseList = function(field, lo, hi) {
            if (field === '*') return null;
            var values = [];
            field.split(',').forEach(function(item) {
                var stepParts = item.split('/');
                var base = stepParts[0];
                var step = stepParts.length > 1 ? Number(stepParts[1]) : 1;
                var range = base === '*' ? [lo, hi] : base.split('-').map(Number);
                var end = range.length > 1 ? range[1] : (stepParts.length > 1 ? hi : range[0]);
                for (var v = range[0]; v <= end && step > 0; v += step) {
                    if (values.indexOf(v) === -1) values.push(v);
                }
            });
            return values;
        };

        var mins = parseList(minField, 0, 59);
        var hours = parseList(hourField, 0, 23);
        var doms = parseList(domField, 1, 31);
        var months = parseList(monthField, 1, 12);
        //cron allows 7 for Sunday
        var dows = parseList(dowField, 0, 6);
        if (dows) dows = dows.map(function(d){ return d % 7; }).filter(function(d, i, all){ return all.indexOf(d) === i; });

        // Check for interval pattern
        if (domField === '*' && monthField === '*' && mins) {
            var sorted = mins.slice().sort(function(a,b){return a-b;});
            var isInterval = false;
            var spacing = 0;
            if (sorted.length > 1) {
                spacing = sorted[1] - sorted[0];
                isInterval = [1,2,3,5,10,15,20,30].indexOf(spacing) >= 0;
                if (isInterval) {
                    for (var i = 1; i < sorted.length; i++) {
                        if (sorted[i] - sorted[i-1] !== spacing) { isInterval = false; break; }
                    }
                    // Verify full coverage: expected count = 60/spacing
                    if (isInterval) {
                        var isContiguousHours = hourField === '*' || (hours && this._isContiguousArray(hours));
                        if (!isContiguousHours) isInterval = false;

                        // For full coverage intervals, sorted must start at 0
                        if (isInterval && sorted[0] !== 0 && sorted.length === Math.floor(60/spacing)) {
                            isInterval = false; // offset pattern, not standard
                        }
                    }
                }
            }
            // Single minute with all hours = hourly (interval 60)
            if (!isInterval && sorted.length === 1 && (hourField === '*' || (hours && hours.length > 1))) {
                var isHourContiguous = hourField === '*' || (hours && this._isContiguousArray(hours));
                if (isHourContiguous) {
                    isInterval = true;
                    spacing = 60;
                }
            }

            if (isInterval) {
                var hStart = 0, hEnd = 23;
                if (hours) {
                    var hs = hours.slice().sort(function(a,b){return a-b;});
                    hStart = hs[0];
                    hEnd = hs[hs.length - 1];
                }
                return {
                    frequency: 'interval',
                    times: [],
                    selectedDays: dows || [],
                    selectedDoms: [],
                    selectedMonths: [],
                    interval: {
                        n: spacing,
                        offset: spacing === 60 ? sorted[0] : 0,
                        hourStart: hourField === '*' ? null : hStart,
                        hourEnd: hourField === '*' ? null : hEnd
                    }
                };
            }
        }

        // Non-interval: expand mins × hours into times[]
        var times = [];
        var mList = mins || [0];
        var hList = hours || [12];
        for (var hi = 0; hi < hList.length; hi++) {
            for (var mi = 0; mi < mList.length; mi++) {
                times.push({hour: hList[hi], minute: mList[mi]});
            }
        }
        // Sort times
        times.sort(function(a,b) { return a.hour*60+a.minute - (b.hour*60+b.minute); });

        if (monthField !== '*') {
            var months0 = (months || []).map(function(m){ return m - 1; });
            return {frequency: 'yearly', times: times, selectedDays: dows || [], selectedDoms: doms || [], selectedMonths: months0};
        }
        if (domField !== '*') {
            return {frequency: 'monthly', times: times, selectedDays: dows || [], selectedDoms: doms || [], selectedMonths: []};
        }
        if (dowField !== '*') {
            return {frequency: 'weekly', times: times, selectedDays: dows || [], selectedDoms: [], selectedMonths: []};
        }
        return {frequency: 'daily', times: times, selectedDays: [], selectedDoms: [], selectedMonths: []};
    },

    scheduleConfigToCron: function(config) {
        if (config.frequency === 'interval') {
            var iv = config.interval;
            var n = iv.n;
            var minList;
            if (n === 60) {
                minList = [iv.offset || 0];
            } else {
                minList = [];
                for (var m = 0; m < 60; m += n) { minList.push(m); }
            }
            var hourPart = '*';
            if (iv.hourStart !== null && iv.hourStart !== undefined && iv.hourEnd !== null && iv.hourEnd !== undefined) {
                var hArr = [];
                for (var h = iv.hourStart; h <= iv.hourEnd; h++) { hArr.push(h); }
                hourPart = hArr.join(',');
            }
            var dowPart = (config.selectedDays && config.selectedDays.length > 0) ? config.selectedDays.join(',') : '*';
            return minList.join(',') + ' ' + hourPart + ' * * ' + dowPart;
        }

        // Non-interval: extract unique mins and hours from times[]
        var minSet = {}, hourSet = {};
        var times = config.times && config.times.length > 0 ? config.times : [{hour:12,minute:0}];
        for (var i = 0; i < times.length; i++) {
            minSet[times[i].minute] = true;
            hourSet[times[i].hour] = true;
        }
        var mins = Object.keys(minSet).map(Number).sort(function(a,b){return a-b;});
        var hours = Object.keys(hourSet).map(Number).sort(function(a,b){return a-b;});
        var minPart = mins.join(',');
        var hourPart2 = hours.join(',');

        var domPart = '*', monthPart = '*', dowPart2 = '*';
        if (config.frequency === 'yearly') {
            domPart = (config.selectedDoms && config.selectedDoms.length > 0) ? config.selectedDoms.join(',') : '*';
            monthPart = (config.selectedMonths && config.selectedMonths.length > 0) ? config.selectedMonths.map(function(m){ return m + 1; }).join(',') : '*';
            dowPart2 = (config.selectedDays && config.selectedDays.length > 0) ? config.selectedDays.join(',') : '*';
        } else if (config.frequency === 'monthly') {
            domPart = (config.selectedDoms && config.selectedDoms.length > 0) ? config.selectedDoms.join(',') : '*';
        } else if (config.frequency === 'weekly') {
            dowPart2 = (config.selectedDays && config.selectedDays.length > 0) ? config.selectedDays.join(',') : '*';
        }

        return minPart + ' ' + hourPart2 + ' ' + domPart + ' ' + monthPart + ' ' + dowPart2;
    },

    cronToHumanReadable: function(cronString) {
        var config = this.cronToScheduleConfig(cronString);
        var DOW_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
        var MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        var self = this;

        var formatTime = function(h, m) {
            var ampm = h >= 12 ? 'PM' : 'AM';
            var h12 = h % 12 || 12;
            var mm = m < 10 ? '0' + m : '' + m;
            return h12 + ':' + mm + ' ' + ampm;
        };

        var formatHour = function(h) {
            var ampm = h >= 12 ? 'PM' : 'AM';
            var h12 = h % 12 || 12;
            return h12 + ' ' + ampm;
        };

        if (config.frequency === 'interval') {
            var iv = config.interval;
            var parts = [];
            if (iv.n === 60) {
                parts.push('Every hour');
                if (iv.offset > 0) parts[0] += ' at :' + (iv.offset < 10 ? '0' + iv.offset : iv.offset);
            } else {
                parts.push('Every ' + iv.n + ' minutes');
            }
            if (iv.hourStart !== null && iv.hourStart !== undefined) {
                parts.push(formatHour(iv.hourStart) + ' – ' + formatHour(iv.hourEnd));
            }
            if (config.selectedDays && config.selectedDays.length > 0 && config.selectedDays.length < 7) {
                if (self._isWeekdays(config.selectedDays)) {
                    parts.push('weekdays');
                } else {
                    parts.push(config.selectedDays.map(function(d){ return DOW_NAMES[d]; }).join(', '));
                }
            }
            return parts.join(', ');
        }

        var timeStrs = config.times.map(function(t){ return formatTime(t.hour, t.minute); });
        // Deduplicate
        var seen = {};
        timeStrs = timeStrs.filter(function(t){ if(seen[t]) return false; seen[t]=true; return true; });
        var timeStr = timeStrs.join(', ');

        if (config.frequency === 'daily') {
            return 'Every day at ' + timeStr;
        }

        if (config.frequency === 'weekly') {
            if (self._isWeekdays(config.selectedDays)) {
                return 'Every weekday at ' + timeStr;
            }
            if (config.selectedDays.length === 2 && config.selectedDays.indexOf(0) >= 0 && config.selectedDays.indexOf(6) >= 0) {
                return 'Every weekend at ' + timeStr;
            }
            var dayNames2 = config.selectedDays.map(function(d){ return DOW_NAMES[d]; });
            return 'Every ' + dayNames2.join(', ') + ' at ' + timeStr;
        }

        if (config.frequency === 'monthly') {
            if (config.selectedDoms.length === 0) {
                return 'Monthly at ' + timeStr;
            }
            var domStrs = config.selectedDoms.map(function(d){ return self.ordinal(d); });
            return 'On the ' + domStrs.join(', ') + ' of every month at ' + timeStr;
        }

        if (config.frequency === 'yearly') {
            var parts2 = [];
            if (config.selectedMonths.length > 0 && config.selectedMonths.length < 12) {
                parts2.push(config.selectedMonths.map(function(m){ return MONTH_NAMES[m]; }).join(', '));
            }
            if (config.selectedDoms.length > 0) {
                parts2.push(config.selectedDoms.map(function(d){ return self.ordinal(d); }).join(', '));
            }
            if (config.selectedDays.length > 0 && config.selectedDays.length < 7) {
                parts2.push(config.selectedDays.map(function(d){ return DOW_NAMES[d]; }).join(', '));
            }
            return (parts2.length > 0 ? parts2.join(' ') : 'Yearly') + ' at ' + timeStr;
        }

        return cronString;
    },

    addScheduleCard: function(container, index, config) {
        var DOW_LABELS = ['S','M','T','W','T','F','S'];
        var MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        var INTERVAL_OPTIONS = [1,2,3,5,10,15,20,30,60];

        var card = document.createElement('div');
        card.className = 'vts-card';
        card.dataset.cardIndex = index;

        // Header
        var header = document.createElement('div');
        header.className = 'vts-card-header';
        header.innerHTML = '<span class="vts-card-title">Schedule #' + (index + 1) + '</span>' +
            '<span class="vts-card-remove" title="Remove">&times;</span>';
        card.appendChild(header);

        // Frequency dropdown
        var freqRow = document.createElement('div');
        freqRow.className = 'vts-card-row';
        freqRow.innerHTML = '<span class="vts-card-label">Repeats</span>';
        var freqSel = document.createElement('select');
        freqSel.className = 'vts-freq-select';
        ['daily','weekly','monthly','yearly','interval'].forEach(function(f){
            var opt = document.createElement('option');
            opt.value = f;
            opt.textContent = f.charAt(0).toUpperCase() + f.slice(1);
            freqSel.appendChild(opt);
        });
        freqSel.value = config.frequency;
        freqRow.appendChild(freqSel);
        card.appendChild(freqRow);

        // Time section (hidden for interval)
        var timeSection = document.createElement('div');
        timeSection.className = 'vts-time-section';
        if (config.frequency === 'interval') timeSection.style.display = 'none';

        var timeLabel = document.createElement('div');
        timeLabel.className = 'vts-card-row';
        timeLabel.innerHTML = '<span class="vts-card-label">Time</span>';

        var timeList = document.createElement('div');
        timeList.className = 'vts-time-list';

        var times = config.times && config.times.length > 0 ? config.times : [{hour:12, minute:0}];
        for (var t = 0; t < times.length; t++) {
            this._appendTimeRow(timeList, times[t]);
        }

        var addTimeBtn = document.createElement('span');
        addTimeBtn.className = 'vts-add-time';
        addTimeBtn.textContent = '+ Add Time';

        timeLabel.appendChild(timeList);
        timeLabel.appendChild(addTimeBtn);
        timeSection.appendChild(timeLabel);
        card.appendChild(timeSection);

        // DOW toggles (for weekly, yearly, interval)
        var dowItems = [];
        for (var d = 0; d < 7; d++) dowItems.push({value: d, label: DOW_LABELS[d]});
        card.appendChild(this._createToggleSection('vts-dow-section', 'On', 'vts-day-toggles', dowItems, config.selectedDays));

        // DOM grid (for monthly, yearly)
        var domItems = [];
        for (var dm = 1; dm <= 31; dm++) domItems.push({value: dm, label: '' + dm});
        card.appendChild(this._createToggleSection('vts-dom-section', 'Days', 'vts-dom-grid', domItems, config.selectedDoms, 'vts-toggle-dom'));

        // Month toggles (for yearly)
        var monthItems = [];
        for (var mo = 0; mo < 12; mo++) monthItems.push({value: mo, label: MONTH_LABELS[mo]});
        card.appendChild(this._createToggleSection('vts-month-section', 'Months', 'vts-month-toggles', monthItems, config.selectedMonths));

        // Interval settings
        var ivSection = document.createElement('div');
        ivSection.className = 'vts-interval-settings';
        var ivRow = document.createElement('div');
        ivRow.className = 'vts-card-row';
        ivRow.innerHTML = '<span class="vts-card-label">Every</span>';
        var ivSel = document.createElement('select');
        ivSel.className = 'vts-interval-n';
        INTERVAL_OPTIONS.forEach(function(n) {
            var opt = document.createElement('option');
            opt.value = n;
            opt.textContent = n;
            ivSel.appendChild(opt);
        });
        ivSel.value = (config.interval && config.interval.n) || 15;
        var ivUnit = document.createElement('span');
        ivUnit.className = 'vts-interval-unit';
        ivUnit.textContent = ' minutes';
        ivRow.appendChild(ivSel);
        ivRow.appendChild(ivUnit);
        ivSection.appendChild(ivRow);

        // At minute (for hourly / n=60)
        var offsetRow = document.createElement('div');
        offsetRow.className = 'vts-card-row vts-offset-row';
        offsetRow.innerHTML = '<span class="vts-card-label">At min</span>';
        var offsetSel = document.createElement('select');
        offsetSel.className = 'vts-interval-offset';
        for (var om = 0; om < 60; om++) {
            var oopt = document.createElement('option');
            oopt.value = om;
            oopt.textContent = ':' + (om < 10 ? '0' + om : om);
            offsetSel.appendChild(oopt);
        }
        offsetSel.value = (config.interval && config.interval.offset) || 0;
        offsetRow.appendChild(offsetSel);
        if (!config.interval || config.interval.n !== 60) offsetRow.style.display = 'none';
        ivSection.appendChild(offsetRow);

        // Hour range
        var hourRow = document.createElement('div');
        hourRow.className = 'vts-card-row';
        hourRow.innerHTML = '<span class="vts-card-label">During</span>';
        var hourMode = document.createElement('select');
        hourMode.className = 'vts-hour-mode';
        var hmAll = document.createElement('option');
        hmAll.value = 'all';
        hmAll.textContent = 'All hours';
        var hmCustom = document.createElement('option');
        hmCustom.value = 'custom';
        hmCustom.textContent = 'Custom range';
        hourMode.appendChild(hmAll);
        hourMode.appendChild(hmCustom);
        hourRow.appendChild(hourMode);

        var hourRange = document.createElement('span');
        hourRange.className = 'vts-hour-range';
        hourRange.style.display = 'none';
        var hStartSel = document.createElement('select');
        hStartSel.className = 'vts-hour-start';
        var hEndSel = document.createElement('select');
        hEndSel.className = 'vts-hour-end';
        for (var hr = 0; hr < 24; hr++) {
            var ampm = hr >= 12 ? 'PM' : 'AM';
            var h12 = hr % 12 || 12;
            var label = h12 + ' ' + ampm;
            var so = document.createElement('option');
            so.value = hr;
            so.textContent = label;
            hStartSel.appendChild(so);
            var eo = document.createElement('option');
            eo.value = hr;
            eo.textContent = label;
            hEndSel.appendChild(eo);
        }
        hourRange.appendChild(hStartSel);
        var toSpan = document.createElement('span');
        toSpan.textContent = ' to ';
        hourRange.appendChild(toSpan);
        hourRange.appendChild(hEndSel);
        hourRow.appendChild(hourRange);
        ivSection.appendChild(hourRow);

        if (config.interval && config.interval.hourStart !== null && config.interval.hourStart !== undefined) {
            hourMode.value = 'custom';
            hourRange.style.display = 'inline';
            hStartSel.value = config.interval.hourStart;
            hEndSel.value = config.interval.hourEnd;
        }

        card.appendChild(ivSection);

        // Show/hide sections based on frequency
        this._updateCardVisibility(card, config.frequency);

        container.appendChild(card);
        this.wireScheduleCardEvents(card);

        return card;
    },

    _appendTimeRow: function(timeList, time) {
        var row = document.createElement('div');
        row.className = 'vts-time-row';

        var hourSel = document.createElement('select');
        hourSel.className = 'vts-time-hour';
        for (var h = 1; h <= 12; h++) {
            var opt = document.createElement('option');
            opt.value = h;
            opt.textContent = h;
            hourSel.appendChild(opt);
        }

        var minSel = document.createElement('select');
        minSel.className = 'vts-time-minute';
        for (var m = 0; m < 60; m++) {
            var mopt = document.createElement('option');
            mopt.value = m;
            mopt.textContent = m < 10 ? '0' + m : '' + m;
            minSel.appendChild(mopt);
        }

        var ampmSel = document.createElement('select');
        ampmSel.className = 'vts-time-ampm';
        var amOpt = document.createElement('option');
        amOpt.value = 'AM'; amOpt.textContent = 'AM';
        var pmOpt = document.createElement('option');
        pmOpt.value = 'PM'; pmOpt.textContent = 'PM';
        ampmSel.appendChild(amOpt);
        ampmSel.appendChild(pmOpt);

        // Set values from 24h
        var h24 = time.hour;
        ampmSel.value = h24 >= 12 ? 'PM' : 'AM';
        hourSel.value = h24 % 12 || 12;
        minSel.value = time.minute;

        var removeBtn = document.createElement('span');
        removeBtn.className = 'vts-time-remove';
        removeBtn.textContent = '\u00D7';

        row.appendChild(hourSel);
        var colon = document.createElement('span');
        colon.textContent = ':';
        colon.className = 'vts-time-colon';
        row.appendChild(colon);
        row.appendChild(minSel);
        row.appendChild(ampmSel);
        row.appendChild(removeBtn);

        timeList.appendChild(row);
    },

    _updateCardVisibility: function(card, freq) {
        var timeSection = card.querySelector('.vts-time-section');
        var dowSection = card.querySelector('.vts-dow-section');
        var domSection = card.querySelector('.vts-dom-section');
        var monthSection = card.querySelector('.vts-month-section');
        var ivSection = card.querySelector('.vts-interval-settings');

        timeSection.style.display = freq === 'interval' ? 'none' : '';
        dowSection.style.display = (freq === 'weekly' || freq === 'yearly' || freq === 'interval') ? '' : 'none';
        domSection.style.display = (freq === 'monthly' || freq === 'yearly') ? '' : 'none';
        monthSection.style.display = freq === 'yearly' ? '' : 'none';
        ivSection.style.display = freq === 'interval' ? '' : 'none';
    },

    wireScheduleCardEvents: function(card) {
        var self = this;

        // Frequency change
        var freqSel = card.querySelector('.vts-freq-select');
        freqSel.addEventListener('change', function() {
            self._updateCardVisibility(card, freqSel.value);
        });

        // Toggle buttons (DOW, DOM, Month)
        card.querySelectorAll('.vts-day-toggles, .vts-dom-grid, .vts-month-toggles').forEach(function(container) {
            container.addEventListener('click', function(e) {
                var target = e.target;
                if (!target.classList.contains('vts-toggle')) return;

                if (target.classList.contains('vts-toggle-all')) {
                    var siblings = container.querySelectorAll('.vts-toggle:not(.vts-toggle-all)');
                    var allActive = target.classList.contains('active');
                    siblings.forEach(function(s) {
                        if (allActive) s.classList.remove('active');
                        else s.classList.add('active');
                    });
                    target.classList.toggle('active');
                } else {
                    target.classList.toggle('active');
                    // Sync "All" button
                    var allBtn = container.querySelector('.vts-toggle-all');
                    var siblings2 = container.querySelectorAll('.vts-toggle:not(.vts-toggle-all)');
                    var activeCount = container.querySelectorAll('.vts-toggle.active:not(.vts-toggle-all)').length;
                    if (activeCount === siblings2.length) allBtn.classList.add('active');
                    else allBtn.classList.remove('active');
                }
            });
        });

        // Add time
        var addTimeBtn = card.querySelector('.vts-add-time');
        if (addTimeBtn) {
            addTimeBtn.addEventListener('click', function() {
                var timeList = card.querySelector('.vts-time-list');
                self._appendTimeRow(timeList, {hour: 12, minute: 0});
            });
        }

        // Remove time
        card.addEventListener('click', function(e) {
            if (e.target.classList.contains('vts-time-remove')) {
                var rows = card.querySelectorAll('.vts-time-row');
                if (rows.length > 1) e.target.parentElement.remove();
            }
        });

        // Remove card
        var removeBtn = card.querySelector('.vts-card-remove');
        removeBtn.addEventListener('click', function() {
            var container = card.parentElement;
            card.remove();
            // Renumber remaining cards
            var cards = container.querySelectorAll('.vts-card');
            cards.forEach(function(c, i) {
                c.dataset.cardIndex = i;
                c.querySelector('.vts-card-title').textContent = 'Schedule #' + (i + 1);
            });
            // Hide remove button if only 1 card
            if (cards.length === 1) cards[0].querySelector('.vts-card-remove').style.display = 'none';
        });

        // Interval N change → show/hide offset row
        var ivSel = card.querySelector('.vts-interval-n');
        if (ivSel) {
            ivSel.addEventListener('change', function() {
                var offsetRow = card.querySelector('.vts-offset-row');
                offsetRow.style.display = parseInt(ivSel.value) === 60 ? '' : 'none';
            });
        }

        // Hour mode change
        var hourMode = card.querySelector('.vts-hour-mode');
        if (hourMode) {
            hourMode.addEventListener('change', function() {
                var range = card.querySelector('.vts-hour-range');
                range.style.display = hourMode.value === 'custom' ? 'inline' : 'none';
            });
        }
    },

    _updateRemoveButtonVisibility: function(container) {
        var cards = container.querySelectorAll('.vts-card');
        cards.forEach(function(c) {
            c.querySelector('.vts-card-remove').style.display = '';
        });
    },

    _renderScheduleList: function(listEl){
        if (!listEl) return;
        listEl.innerHTML = '';

        var crons = (app.vyb.triggerSettings && app.vyb.triggerSettings.crons) || [];

        if (crons.length === 0) {
            listEl.innerHTML = '<div class="vts-schedule-empty">No schedules set</div>';
            return;
        }

        for (var i = 0; i < crons.length; i++) {
            var c = crons[i];
            var item = document.createElement('div');
            item.className = 'vts-schedule-item';

            var desc = this.cronToHumanReadable(c.cron);
            var html = '<div class="vts-schedule-desc"><span class="vts-schedule-num">' + (i + 1) + '.</span> ' + desc;
            if (c.timeZone) html += ' <span class="vts-schedule-tz">' + c.timeZone + '</span>';
            html += '</div>';
            html += '<span class="vschedule-delete" data-schedule-index="' + i + '" title="Delete">&times;</span>';

            item.innerHTML = html;
            listEl.appendChild(item);
        }
    },

    configScheduleSettings: function(mode='current', view=null){

        var container = view != null ? view.query('.vts-schedule-edit') : document.querySelector('.vts-schedule-edit');
        if (!container) return;

        // Render saved schedules as read-only list
        var listEl = container.querySelector('.vts-schedule-list');
        this._renderScheduleList(listEl);

        // Render one blank add-card
        var addCardContainer = container.querySelector('[data-hook="schedule_add_card"]');
        if (addCardContainer) {
            addCardContainer.innerHTML = '';
            var defaultConfig = {frequency: 'daily', times: [{hour:12,minute:0}], selectedDays: [], selectedDoms: [], selectedMonths: []};
            var card = this.addScheduleCard(addCardContainer, 0, defaultConfig);
            // Hide card header since parent already says "Add Schedule"
            var cardHeader = card.querySelector('.vts-card-header');
            if (cardHeader) cardHeader.style.display = 'none';
        }

        // Timezone dropdown — default from first saved cron or user's local tz
        var crons = (app.vyb.triggerSettings && app.vyb.triggerSettings.crons) || [];
        var tzSelect = container.querySelector("select[name='cron_timezone']");
        if (tzSelect) {
            var tzDefault = (crons.length > 0 && crons[0].timeZone) ? crons[0].timeZone : null;
            this._populateTimezoneSelect(tzSelect, tzDefault);
        }

        // Exit edit mode when clicking outside the container
        var onDocumentClick = function(e){
            var currentContainer = document.querySelector('.vts-schedule-edit');
            if (!currentContainer || !currentContainer.contains(e.target)) {
                document.removeEventListener('mousedown', onDocumentClick);
                if (view && view.model) view.model.set('editing', false);
                app.header.set('editing', false);
            }
        };
        document.addEventListener('mousedown', onDocumentClick);
    },

    configGeofenceSettings: function(view){

        //currentSettings
        let currentSettings = {};
        if(app.vyb.geofence!=null){
            currentSettings = app.vyb.geofence;
        }

        const clickHereSpan = '<span class="vset">click here to set</span>'

        var params = {
            glocation: currentSettings.lat!=undefined ? currentSettings.lat + ', ' + currentSettings.lon : clickHereSpan,
            gradius: currentSettings.displayRadius!=undefined ? currentSettings.displayRadius : '200',
            gradius_units: currentSettings.radiusUnits!=undefined ? currentSettings.radiusUnits : 'meters',
            gtype: currentSettings.type!=undefined ? currentSettings.type : 'enter',
            gtime: currentSettings.timeThrottle!=undefined ? currentSettings.timeThrottle : '24',
            gsub: currentSettings.subscribable!=undefined ? currentSettings.subscribable : 'no',
        }

        var mapUri = params.glocation==clickHereSpan ? `https://google.com/maps/?q=current+location` : `https://google.com/maps/?q=${app.vyb.geofence.lat},${app.vyb.geofence.lon}`;
        var mapLink = `<a onclick="app.utils.cancelBubble()" href="${mapUri}" target="_blank">map link</a>`;

        view.query("input[name='glocation']").value = params.glocation==clickHereSpan ? '' : params.glocation;
        view.query("input[name='glocation']").placeholder = 'set coordinates in decimal format';
        view.query("input[name='gradius']").value = params.gradius;
        view.query("select[name='gradius_units'] option[value='"+params.gradius_units+"']").selected = true;
        view.query("select[name='gtype'] option[value='"+params.gtype+"']").selected = true;
        view.query("input[name='gtime']").value = params.gtime;
        view.query("select[name='gsub']").value = params.gsub;
        view.query("div[data-hook='gmap']").innerHTML = mapLink;

    },

    manageRating : () => {
        const now = Math.round(new Date().getTime() / 1000);
        //set request dts
        if(app.profile?.permissions?.rating === undefined) {

            var ratingStatus = {
                status: "soak",
                dts: now + app.config?.ratingSoakTime
            }

            let permissions = app.profile.permissions || {};
            permissions.rating = ratingStatus;
            app.profile.set('permissions', permissions);
            app.profile.patch({permissions: permissions});
            app.eventLog.add('setting rating permission:');
            app.eventLog.add(ratingStatus);

            //rating shown/completed
        }else if(app.profile.permissions?.rating?.status === "done"){
            app.eventLog.add('rating was shown/completed');

            //request rating
        }else if(app.profile.permissions?.rating?.status!=="done" && now > app.profile.permissions.rating.dts){
            app.eventLog.add('will ask for rating since ' + now + ' > ' + app.profile.permissions.rating.dts);
            Rating.askForRating()
        }else{
            app.eventLog.add('rating ask still soaking since ' + now + ' < ' + app.profile.permissions.rating.dts);
        }
    },

    //log is plain text, escaped wherever it is shown, so it keeps its tags
    stripFieldHtml: function(key, value) {
        return key=="log" ? value : logText.stripTags(value);
    },

    isValidURL: (string) => {
        // Allow localhost and 127.0.0.1 for local development
        if (string.match(/^https?:\/\/(localhost|127\.0\.0\.1)(:[0-9]+)?(\/.*)?$/)) {
            return true;
        }
        // Standard URL validation for production URLs
        var res = string.match(/https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z]{2,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g);
        return (res !== null);
    },

    // ---- Shared trigger UI helpers ----

    _populateTimezoneSelect: function(selectEl, defaultValue){
        if (!selectEl) return;
        selectEl.innerHTML = '';
        var timeZones = moment.tz.names();
        for (var z = 0; z < timeZones.length; z++) {
            var opt = document.createElement('option');
            opt.value = timeZones[z];
            opt.textContent = timeZones[z];
            selectEl.appendChild(opt);
        }
        selectEl.value = defaultValue || moment.tz.guess();
    },

    // ---- Reminder UI helpers ----

    configReminderSettings: function(view){
        var container = view != null ? view.query('.vts-reminder-edit') : document.querySelector('.vts-reminder-edit');
        if (!container) return;

        // Exit edit mode when clicking outside the container (same as blur on regular params)
        var onDocumentClick = function(e){
            var currentContainer = document.querySelector('.vts-reminder-edit');
            if (!currentContainer || !currentContainer.contains(e.target)) {
                document.removeEventListener('mousedown', onDocumentClick);
                if (view && view.model) view.model.set('editing', false);
                app.header.set('editing', false);
            }
        };
        document.addEventListener('mousedown', onDocumentClick);

        // Populate hour selector
        var hourSel = container.querySelector('.vreminder-hour');
        if (hourSel) {
            hourSel.innerHTML = '';
            for (var h = 1; h <= 12; h++) {
                var opt = document.createElement('option');
                opt.value = h;
                opt.textContent = h;
                hourSel.appendChild(opt);
            }
        }

        // Populate minute selector
        var minSel = container.querySelector('.vreminder-minute');
        if (minSel) {
            minSel.innerHTML = '';
            for (var m = 0; m < 60; m++) {
                var mopt = document.createElement('option');
                mopt.value = m;
                mopt.textContent = m < 10 ? '0' + m : '' + m;
                minSel.appendChild(mopt);
            }
        }

        // Populate timezone dropdown
        this._populateTimezoneSelect(container.querySelector('.vreminder-timezone'));

        var messageEl = container.querySelector('.vreminder-message');
        if (messageEl) messageEl.maxLength = limits.MESSAGE_MAX_LENGTH;

        // Prefill date and time with now + 5 minutes
        var prefill = moment().add(5, 'minutes');
        var dateInput = container.querySelector('.vreminder-date-input');
        if (dateInput) {
            dateInput.setAttribute('min', moment().format('YYYY-MM-DD'));
            dateInput.setAttribute('max', moment().add(1, 'year').format('YYYY-MM-DD'));
            dateInput.value = prefill.format('YYYY-MM-DD');
        }
        var ampmSel = container.querySelector('.vreminder-ampm');
        if (hourSel && minSel && ampmSel) {
            var h24 = prefill.hour();
            ampmSel.value = h24 >= 12 ? 'PM' : 'AM';
            hourSel.value = h24 % 12 || 12;
            minSel.value = prefill.minute();
        }

        // Fetch reminders from server (triggers GC of past reminders) then render
        var listEl = container.querySelector('.vts-reminder-list');
        var self = this;
        jQuery.ajax(Object.assign({
            url: app.config.restApiUrl + '/vyb/' + app.vyb.key + '/reminders',
            type: 'GET',
            success: function(response) {
                if (response.result === 1) {
                    var ts = Object.assign({}, app.vyb.triggerSettings || {});
                    ts.reminders = response.reminders;
                    app.vyb.set('triggerSettings', ts, {silent: true});
                }
                self._renderReminderList(listEl);
            },
            error: function() {
                self._renderReminderList(listEl);
            }
        }, this.setAuthTokenHeader()));
    },

    _renderReminderList: function(listEl){
        if (!listEl) return;
        listEl.innerHTML = '';

        var reminders = (app.vyb.triggerSettings && app.vyb.triggerSettings.reminders) || [];

        if (reminders.length === 0) {
            listEl.innerHTML = '<div class="vts-reminder-empty">No reminders set</div>';
            return;
        }

        for (var i = 0; i < reminders.length; i++) {
            var r = reminders[i];
            var item = document.createElement('div');
            item.className = 'vts-reminder-item';

            var dateDisplay = this._cronToDateDisplay(r.cron, r.timeZone, r.year);

            var html = '<div class="vts-reminder-datetime">' + dateDisplay;
            if (r.timeZone) html += ' <span class="vts-reminder-tz">' + r.timeZone + '</span>';
            if (r.message) {
                var truncMsg = r.message.length > 40 ? r.message.substring(0, 40) + '...' : r.message;
                html += ' <span class="vts-reminder-msg">' + truncMsg + '</span>';
            }
            html += '</div>';

            if (r.imageUrl) html += ' <a class="vts-reminder-msg" href="' + r.imageUrl + '" target="_blank" onclick="app.utils.cancelBubble()">[img]</a>';
            if (r.linkUrl) html += ' <a class="vts-reminder-msg" href="' + r.linkUrl + '" target="_blank" onclick="app.utils.cancelBubble()">[link]</a>';

            html += '<span class="vreminder-delete" data-reminder-id="' + r.id + '" title="Delete">&times;</span>';

            item.innerHTML = html;
            listEl.appendChild(item);
        }
    },

    _cronToDateDisplay: function(cronString, timeZone, year){
        var MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        var parts = cronString.trim().split(/\s+/);
        //legacy recurring reminders have no single date to show
        if (!/^\d{1,2}$/.test(parts[2]) || !/^\d{1,2}$/.test(parts[3])) return this.cronToHumanReadable(cronString);
        var minute = parseInt(parts[0]);
        var hour = parseInt(parts[1]);
        var day = parseInt(parts[2]);
        var month = parseInt(parts[3]);

        // Format time
        var ampm = hour >= 12 ? 'PM' : 'AM';
        var h12 = hour % 12 || 12;
        var mm = minute < 10 ? '0' + minute : '' + minute;
        var timeStr = h12 + ':' + mm + ' ' + ampm;

        // Use stored year; fall back to guessing if not available (legacy reminders)
        if (!year) {
            var now = timeZone ? moment.tz(timeZone) : moment();
            year = now.year();
            var reminderDate = timeZone ? moment.tz({year: year, month: month - 1, date: day, hour: hour, minute: minute}, timeZone) : moment({year: year, month: month - 1, date: day, hour: hour, minute: minute});
            if (reminderDate.isBefore(now)) year += 1;
        }

        return MONTH_NAMES[month - 1] + ' ' + day + ', ' + year + ' at ' + timeStr;
    },

    clipboard: () => {
        if(app.clipboard!=undefined) app.clipboard.destroy();
        app.clipboard = new Clipboard('button.vcopybutton');
        app.clipboard.on('success', function (e) {
            const element = e.trigger;
            element.innerText = "COPIED";
            setTimeout(function(){element.innerText = "COPY"; },4000);
            e.clearSelection();
        });
        app.clipboard.on('error', function (e) {
            console.error(e);
        });
    }
}